<template>
  <div>
    <va-create v-if="getterPopupName === 'VaCreate' && !vaId"
        @refreshList="updateFilters"
        :self-va-enabled="selfVaEnabled.manage" />
    <va-export-filter v-else-if="['VaFilter', 'VaExport'].includes(getterPopupName)"
        @updateFilters="updateFilters"
        :vaId="vaId" />

    <div class="table-title">
      <div class="subject">
        <p>Virtual Accounts</p>
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('VaCreate')" v-if="manageAccess.va && !this.vaId" vt-data="create">
            <span>{{isDesktop() ? 'Add Virtual Account' : 'Add'}}</span>
            <i class="fa fa-plus" />
          </button>
          <button @click="openPopup('VaExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button @click="openPopup('VaFilter')" :class="{'filters-applied': filtersApplied}"
            class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <va-search v-if="!vaId" placeholder="Search ID / Ref number / Account number"
                :triggerSearch="triggerSearch" :searchTerm="searchTerm"
                @populateList="clearSearch"
                @setFunctions="setSearchFunctions" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p>ID</p>
      <p>Created Time</p>
      <p>Reference Number</p>
      <p>UDF1</p>
      <p>UDF2</p>
      <p>UDF3</p>
      <p v-if="selfVaEnabled.read">Account Balance</p>
      <p>Status</p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(va, index) in list" :key="index"
        @click="openTransactions(va.id)"
      >
        <p class="mt-grey-text">
          <b v-if="!isDesktop()">ID:</b>
          <span>{{va.id}}</span>
        </p>
        <p class="mt-grey-text">{{va.created_at}}</p>

        <p>
          <b v-if="!isDesktop()">Ref Num:</b>
          <marquee v-if="va.reference_number.length >= 20"
                  :scrollamount="4">
            <span>{{va.reference_number}}</span>
          </marquee>
          <span class="pre-space" v-else>{{va.reference_number}}</span>
        </p>

        <p>
          <b v-if="!isDesktop()">UDF1:</b>
          <marquee v-if="va.udf1.length >= 20"
                  :scrollamount="4">
            {{va.udf1}}
          </marquee>
          <span class="pre-space" v-else>{{va.udf1}}</span>
        </p>

        <p>
          <b v-if="!isDesktop()">UDF2:</b>
          <marquee v-if="va.udf2.length >= 20" :scrollamount="4">
            {{va.udf2}}
          </marquee>
          <span class="pre-space" v-else>{{va.udf2}}</span>
        </p>

        <p>
          <b v-if="!isDesktop()">UDF3:</b>
          <marquee v-if="va.udf3.length >= 20" :scrollamount="4">
            {{va.udf3}}
          </marquee>
          <span class="pre-space" v-else>{{va.udf3}}</span>
        </p>

        <p v-if="selfVaEnabled.read">{{showBalance(va.account_balance)}}</p>

        <p class="mt-grey-text">
          {{va.status}}
        </p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || !paginate.stop" />

    <div class="table-empty" v-else-if="list.length === 0">
      <p v-if="searchTerm">Unable to find Virtual Account with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / Reference Number / Account Number.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>

    <promotion-banner
      :banner-img="BannerImg"
    />
  </div>
</template>

<script>
import VaCreate from '@/components/VirtualAccounts/Form'
import VaExportFilter from '@/components/VirtualAccounts/VAExportFilter'
import VaSearch from '@/components/global/SearchRecords'
import PromotionBanner from '@/components/global/PromotionBanner'

import BannerImg from '@/assets/images/GCP_2.gif'

import ApiServices from '@/services/api/virtualAccounts'
import listMixins from '@/mixins/listMixins'
import { amountFormat } from '@/utils/helpers'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'VAList',
  components: { VaCreate, VaExportFilter, VaSearch, PromotionBanner },
  mixins: [listMixins],
  props: {
    vaId: {
      type: [String, Boolean, Number],
      default: false
    }
  },
  data () {
    return {
      BannerImg,
      manageAccess: {},
      selfVaEnabled: {
        manage: false,
        read: false
      }
    }
  },
  computed: {
    ...mapGetters(['getterSectionDetails'])
  },
  created () {
    this.startProcessing()
  },
  beforeDestroy () {
    if (!this.vaId) {
      this.SET_SECTION_DETAILS(null)
    }
  },
  methods: {
    amountFormat,
    ...mapMutations(['SET_VA_CONNECTED_BANKING', 'SET_UNPRIVILEGE_PAGE']),
    populateParams () {
      if (this.vaId) {
        this.params['search'] = {
          parent_id: this.vaId
        }
      }

      this.keys['list'] = 'virtual_accounts'
      this.api['search'] = ApiServices.search
      this.setHighlightSelectors([1, 3])
      this.paginate['type'] = 'virtual account'
      this.params['filtersLen'] = 0
      this.paginate['token_type'] = 'next_page_token'
      this.paginate['no_search_export'] = true
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['next_page_token'] = this.paginate['next_page_token']
        } else if (this.vaId) {
          params['fetch_va_details'] = !this.getterSectionDetails
        }

        if (this.vaId) {
          params['parent_id'] = this.vaId
        }

        ApiServices.virtualAccountsList(params).then((response) => {
          this.SET_STATEMENT_FILTER(this.filterOptions)

          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.virtual_accounts)
              this.checkPaginateV2(data.next_page_token)
            } else {
              this.list = data.virtual_accounts
              this.setPaginate(data)
              this.populateMeta(response)
            }

            this.setAccess(response.privileges)
            resolve()
          } else if (this.vaId) {
            this.SET_UNPRIVILEGE_PAGE('Virtual Account Details')
          }
        }).catch(() => {
          if (this.vaId) {
            this.$router.push({name: 'VAList'})
          }
        })
      })
    },
    openTransactions (id) {
      this.$router.push({name: 'VATransactions', params: {id}})
    },
    populateMeta ({data, privileges}, fromSearch) {
      this.selfVaEnabled = {
        manage: data.self_va_enabled,
        read: data.self_va_read
      }

      if (this.vaId) {
        if (!this.getterSectionDetails) {
          this.SET_SECTION_DETAILS({
            va: data.virtual_account,
            privileges,
            wl_limit: data.whitelist_rmtr_limit,
            self_va_enabled: data.self_va_enabled
          })
        }
        return
      }

      this.SET_SECTION_DETAILS({
        wl_limit: data.whitelist_rmtr_limit
      })
      this.SET_VA_CONNECTED_BANKING(data.connected_banking_enabled)

      if (fromSearch) {
        this.setAccess(privileges)
      }
    },
    setAccess (privileges) {
      this.manageAccess = {
        va: privileges.manage_virtual_accounts,
        va_balance: privileges.va_balance
      }
    },
    showBalance (bal) {
      if (!this.manageAccess.va_balance) {
        return '-'
      }
      return (bal !== 'NA') ? this.amountFormat(bal) : 'NA'
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .mobile, .tablet {
    .table-row {
      p {
        &:nth-child(even) {
          text-align: right !important;
        }
      }
    }
  }
</style>
